import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { formatDate } from '../i18n.js';
import { useMe } from '../session.js';
import { Button, Card, Notice } from './ui.js';

type Method = 'dns_txt' | 'meta_tag' | 'file';
const METHODS: Method[] = ['meta_tag', 'dns_txt', 'file'];
/** Step-by-step guides for the hosts and systems most used in Germany (T-26). */
const GUIDES: Record<Method, string[]> = {
  meta_tag: ['wordpress', 'jimdo', 'other'],
  dns_txt: ['ionos', 'strato', 'allinkl', 'other'],
  file: ['ftp', 'other'],
};

function Copy({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-semibold">{label}</span>
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 break-all rounded bg-slate-100 px-2 py-1 font-mono text-sm">
          {value}
        </code>
        <Button
          variant="secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => setCopied(true));
          }}
          aria-label={t('verify.copyLabel', { label })}
        >
          {copied ? t('verify.copied') : t('verify.copy')}
        </Button>
      </div>
    </div>
  );
}

/** Ownership check in the site settings (architecture §10.2): lifts the 25-page limit. */
export function Verification({ site }: { site: Schemas['SiteDetail'] }) {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const client = useQueryClient();
  const [method, setMethod] = useState<Method>('meta_tag');
  const status = useQuery({
    queryKey: ['site', site.id, 'verification'],
    queryFn: () =>
      unwrap(api.GET('/sites/{siteId}/verification', { params: { path: { siteId: site.id } } })),
    enabled: !site.verified,
  });
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/sites/{siteId}/verification-challenges', {
          params: { path: { siteId: site.id } },
          body: { method },
        }),
      ),
    onSuccess: () => {
      verify.reset();
      void status.refetch();
    },
  });
  const verify = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/sites/{siteId}/verify', { params: { path: { siteId: site.id } } })),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', site.id] }),
  });

  if (site.verified || verify.isSuccess)
    return (
      <Card>
        <h2 className="mb-2 text-xl font-semibold">{t('settings.verification.title')}</h2>
        <Notice kind="success">{t('settings.verification.done')}</Notice>
      </Card>
    );

  const c = create.data;
  const active = status.data?.activeChallenge;
  const hint =
    verify.error instanceof ApiError && verify.error.code === 'NOT_VERIFIED'
      ? t(`verify.hints.${String((verify.error.problem as { hint?: string }).hint)}`, {
          defaultValue: t('verify.hints.unreachable'),
        })
      : verify.error
        ? errorMessage(verify.error, t)
        : null;

  return (
    <Card>
      <h2 className="mb-2 text-xl font-semibold">{t('settings.verification.title')}</h2>
      <Notice kind="warning">{t('settings.verification.missing')}</Notice>
      <fieldset className="mt-4 flex flex-col gap-2">
        <legend className="mb-1 font-semibold">{t('verify.method')}</legend>
        {METHODS.map((m) => (
          <label key={m} className="flex min-h-11 items-start gap-3">
            <input
              type="radio"
              name={`verify-${site.id}`}
              value={m}
              checked={method === m}
              onChange={() => setMethod(m)}
              className="mt-1 h-5 w-5 accent-blue-700"
            />
            <span>
              <span className="font-medium">{t(`verify.methods.${m}.title`)}</span>
              <br />
              <span className="text-sm text-slate-700">{t(`verify.methods.${m}.text`)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <Button className="mt-3" busy={create.isPending} onClick={() => create.mutate()}>
        {c || active ? t('verify.newCode') : t('verify.createCode')}
      </Button>
      {create.isError ? <Notice kind="error">{errorMessage(create.error, t)}</Notice> : null}

      {c ? (
        <div className="mt-4 flex flex-col gap-3 rounded-md border border-slate-200 p-3">
          <p className="font-semibold">{t(`verify.methods.${c.method}.step`)}</p>
          {c.method === 'dns_txt' ? (
            <>
              <Copy label={t('verify.dnsName')} value={c.instructions.dnsRecordName} />
              <Copy label={t('verify.dnsValue')} value={c.instructions.dnsRecordValue} />
            </>
          ) : null}
          {c.method === 'meta_tag' ? (
            <Copy label={t('verify.metaTag')} value={c.instructions.metaTag} />
          ) : null}
          {c.method === 'file' ? (
            <>
              <Copy label={t('verify.filePath')} value={c.instructions.filePath} />
              <Copy label={t('verify.fileContent')} value={c.instructions.fileContent} />
            </>
          ) : null}
          <p className="text-sm text-slate-700">
            {t('verify.validUntil', { date: formatDate(c.expiresAt, i18n.language, timeZone) })}
          </p>
          <div className="flex flex-col gap-2">
            {GUIDES[c.method].map((g) => (
              <details key={g} className="rounded-md border border-slate-200 p-2">
                <summary className="min-h-11 cursor-pointer py-2 font-semibold text-blue-900">
                  {t(`verify.guides.${g}.title`)}
                </summary>
                <ol className="ml-5 list-decimal">
                  {(t(`verify.guides.${g}.steps`, { returnObjects: true }) as string[]).map(
                    (step) => (
                      <li key={step}>{step}</li>
                    ),
                  )}
                </ol>
              </details>
            ))}
          </div>
        </div>
      ) : active ? (
        <p className="mt-3 text-sm text-slate-700">
          {t('verify.activeCode', {
            method: t(`verify.methods.${active.method}.title`),
            date: formatDate(active.expiresAt, i18n.language, timeZone),
          })}
        </p>
      ) : null}

      {c || active ? (
        <div className="mt-4 flex flex-col gap-2">
          <div>
            <Button variant="secondary" busy={verify.isPending} onClick={() => verify.mutate()}>
              {t('verify.check')}
            </Button>
          </div>
          {hint ? <Notice kind="error">{hint}</Notice> : null}
        </div>
      ) : null}
    </Card>
  );
}
